import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { drawCheck, fadeUp, reducedMotion, scaleIn } from '../../atome/motion/motion.styled';
import Footer from '../footer/footer';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(70% 60% at 50% 0%, ${color.sandLight} 0%, transparent 60%),
    ${color.cream};
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 32rem;
  padding: 3rem 2.4rem 2.6rem;
  text-align: center;
  border-radius: ${radius.xl};
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: ${shadow.lift};
  animation: ${scaleIn} 620ms ${ease.out} both;

  h1 {
    font-family: ${font.display};
    font-size: clamp(2rem, 4.4vw, 2.7rem);
    font-weight: 600;
    line-height: 1.1;
    color: ${color.ink};
    margin: 1.4rem 0 0.6rem;
    animation: ${fadeUp} 620ms ${ease.out} 120ms both;
  }

  > p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    line-height: 1.75;
    margin: 0 auto 2rem;
    max-width: 34ch;
    animation: ${fadeUp} 620ms ${ease.out} 200ms both;
  }

  @media (max-width: 600px) {
    padding: 2.4rem 1.4rem 2rem;
  }

  ${reducedMotion};
`;

const CheckMark = styled.svg`
  width: 84px;
  height: 84px;
  display: block;
  margin: 0 auto;
  padding: 18px;
  border-radius: 50%;
  background: ${color.forest};
  box-shadow: 0 16px 34px rgba(74, 112, 97, 0.32);
  animation: ${scaleIn} 500ms ${ease.spring} both;

  path {
    fill: none;
    stroke: #fff;
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: ${drawCheck} 620ms ${ease.out} 320ms forwards;
  }

  ${reducedMotion};
`;

const Details = styled.dl`
  display: grid;
  gap: 0.9rem;
  margin: 0 0 2rem;
  padding: 1.4rem 1.5rem;
  text-align: left;
  border-radius: ${radius.lg};
  background: rgba(226, 213, 196, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.8);
  animation: ${fadeUp} 620ms ${ease.out} 280ms both;

  div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  dt {
    font-family: ${font.body};
    font-size: 0.85rem;
    color: ${color.inkSoft};
  }

  dd {
    margin: 0;
    font-family: ${font.body};
    font-size: 0.95rem;
    font-weight: 500;
    color: ${color.ink};
  }

  div:last-child dd {
    font-family: ${font.display};
    font-size: 1.5rem;
    font-weight: 600;
    color: ${color.forest};
  }

  ${reducedMotion};
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.98rem;
  letter-spacing: 0.05em;
  color: #fff;
  background: ${color.forest};
  box-shadow: 0 16px 34px rgba(74, 112, 97, 0.26);
  animation: ${fadeUp} 620ms ${ease.out} 360ms both;
  transition: transform ${duration.base} ${ease.spring}, background ${duration.base} ${ease.out};

  &:hover {
    background: ${color.forestDeep};
    transform: translateY(-3px);
  }

  ${reducedMotion};
`;

const Note = styled.p`
  margin: 1.2rem 0 0;
  font-family: ${font.body};
  font-size: 0.8rem;
  color: ${color.inkFaint};
  animation: ${fadeUp} 620ms ${ease.out} 420ms both;
  ${reducedMotion};
`;

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderResponse } = location.state || {};

  if (!orderResponse) {
    return (
      <PageWrapper>
        <Center>
          <Card>
            <h1>No order to show</h1>
            <p>We could not find order details for this page. Your bag is still waiting for you.</p>
            <PrimaryButton onClick={() => navigate('/')}>Back to the shop</PrimaryButton>
          </Card>
        </Center>
        <Footer />
      </PageWrapper>
    );
  }

  const { orderId, totalQuantity, grandTotal } = orderResponse;

  return (
    <PageWrapper>
      <Center>
        <Card>
          <CheckMark viewBox="0 0 48 48" aria-hidden="true">
            <path d="M12 25.5 L20.5 34 L36 15" />
          </CheckMark>

          <h1>Your order is in</h1>
          <p>
            Thank you — we have your order and will call you to confirm delivery. Payment happens at
            your door.
          </p>

          <Details>
            <div>
              <dt>Order number</dt>
              <dd>#{orderId}</dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{totalQuantity}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>${Number(grandTotal || 0).toFixed(2)}</dd>
            </div>
          </Details>

          <PrimaryButton onClick={() => navigate('/')}>Continue shopping</PrimaryButton>
          <Note>Keep your order number handy if you need to reach us.</Note>
        </Card>
      </Center>
      <Footer />
    </PageWrapper>
  );
};

export default OrderSuccess;
